import { GQL } from "@binale-tech/shared";
import { toast } from "@heroui/react";
import React, {
    createContext,
    type Dispatch,
    type FC,
    type PropsWithChildren,
    type SetStateAction,
    use,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import { useIntl } from "react-intl";

import { DmsType } from "@domain/models/dms";
import { DmsDefaultSubType, type TBindSubType, type TUploadFile, UploadFileStatus } from "@dms/types";

import { useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import { DmsUtils } from "@dms/scripts/utils/DmsUtils";
import type { ALL_DOCUMENTS } from "@dms/configs/constants";
import { useBindSubtype } from "@dms/hooks/useBindSubtype";
import { DocumentsApi } from "@domain/api/DocumentsApi";
import pLimit from "p-limit";
import { CompanyContext } from "@domain/context/CompanyContext";
import { useApolloClient } from "@apollo/client/react";

type TProps = {
    onClose: () => void;
    useSingleDocumentMode?: boolean;
    onAutoCloseAfterUpload?: (documentIds: string[]) => void;
    productData?: { productKey: GQL.IProductKey; selectedRecordGroup: string };
    documentType?: string[];
};

type TValue = {
    allFilesInfo: TUploadFile[];
    documentType?: { type: DmsType | typeof ALL_DOCUMENTS; subType?: string };
    queueIsEmpty: boolean;
    unsupportedFiles: { file: File; reason: string }[];
    useSingleDocumentMode?: boolean;
};

type TActionValue = {
    handleFilesDrop: (arg: Pick<TUploadFile, "file" | "hash">[]) => void;
    setUnsupportedFiles: Dispatch<SetStateAction<{ file: File; reason: string }[]>>;
    deleteAllNotUniq: (processedFiles: Record<string, number>, notUniqFiles: Record<string, TUploadFile[]>) => void;
    confirmChoiceNotUniq: (processedFiles: Record<string, number>, notUniqFiles: Record<string, TUploadFile[]>) => void;
    removeNotUniqGroup: (key: string, notUniqFiles: Record<string, TUploadFile[]>) => void;
    finish: () => void;
};

export const FileUploaderContext = createContext({} as TValue);
export const FileUploaderControlContext = createContext({} as TActionValue);

export const FileUploaderProvider: FC<PropsWithChildren<TProps>> = ({
    children,
    onAutoCloseAfterUpload,
    productData,
    documentType,
    useSingleDocumentMode,
    onClose,
}) => {
    const [allFilesInfo, setAllFilesInfo] = useState<TUploadFile[]>([]);
    const [unsupportedFiles, setUnsupportedFiles] = useState<{ file: File; reason: string }[]>([]);

    const allFilesInfoRef = useRef(allFilesInfo);
    allFilesInfoRef.current = allFilesInfo;

    const onAutoCloseAfterUploadRef = useRef(onAutoCloseAfterUpload);
    onAutoCloseAfterUploadRef.current = onAutoCloseAfterUpload;

    // Hashes of files already in DMS at the time of drop - kept stable across resolutions
    const alreadyUploadedHashesRef = useRef<string[]>([]);
    // Ids handed to onAutoCloseAfterUpload when the user presses Done after a partly failed upload
    const finishIdsRef = useRef<string[]>([]);

    const { formatMessage } = useIntl();
    const documentsMap = useDmsDocumentsMap();
    const { companyGQL } = use(CompanyContext);
    const client = useApolloClient();

    const dmsType = useMemo(() => {
        if (!productData?.productKey) {
            return DmsType.new_documents;
        }
        if (productData.productKey in DmsType) {
            return productData.productKey as unknown as DmsType;
        } else if (productData.productKey === GQL.IProductKey.ErA) {
            return DmsType.ER;
        }
        return DmsType.new_documents;
    }, [productData?.productKey]);

    const subTypeByBoundId = useBindSubtype(productData?.selectedRecordGroup, dmsType as TBindSubType);

    const type = useMemo((): { type: DmsType; subType?: string } => {
        if (documentType && documentType.length) {
            const { type, subType } = DmsUtils.getActiveTypeAndSubType(documentType);
            let checkedSubType: string | undefined;
            if (subType) {
                const isDefaultSubtype =
                    subType.includes(DmsDefaultSubType.all_subTypes) || subType.includes(DmsDefaultSubType.no_subTypes);
                if (!isDefaultSubtype) {
                    checkedSubType = subType;
                }
            }
            return { type: type as DmsType, subType: checkedSubType };
        }
        if (productData?.productKey) {
            const { productKey } = productData;
            let typeByProduct: DmsType;
            if (productKey in DmsType) {
                typeByProduct = productKey as unknown as DmsType;
            } else if (productKey === GQL.IProductKey.ErA) {
                typeByProduct = DmsType.ER;
            } else {
                return { type: DmsType.new_documents };
            }
            return { type: typeByProduct, subType: subTypeByBoundId };
        }
        return { type: DmsType.new_documents };
    }, [documentType, productData?.productKey, subTypeByBoundId]);

    const queueIsEmpty = useMemo(() => {
        if (!allFilesInfo.length) {
            return true;
        }
        return !allFilesInfo.some(
            (file) => file.status === UploadFileStatus.READY_FOR_UPLOAD || file.status === UploadFileStatus.PROGRESS,
        );
    }, [allFilesInfo]);

    const setFileStatus = (key: string, data: Pick<TUploadFile, "status" | "error" | "isUploaded">) => {
        const { status, error, isUploaded } = data;
        setAllFilesInfo((prev) => {
            const arr = [...prev];
            arr.forEach((el) => {
                if (
                    el.hash !== key ||
                    (el.status !== UploadFileStatus.READY_FOR_UPLOAD && el.status !== UploadFileStatus.PROGRESS)
                ) {
                    return;
                }
                el.status = status;
                el.error = error ?? false;
                el.isUploaded = isUploaded;
            });
            return arr;
        });
    };

    const uploadFiles = useCallback(
        async (filesToUpload: TUploadFile[], alreadyUploadedHashes: string[]) => {
            const readyFiles = filesToUpload.filter((f) => f.status === UploadFileStatus.READY_FOR_UPLOAD);

            if (!readyFiles.length && !alreadyUploadedHashes.length) {
                return;
            }

            const limit = pLimit(16);
            const results = await Promise.allSettled(
                readyFiles.map((fileData) =>
                    limit(() =>
                        DocumentsApi.createDocument({
                            fileUploadData: {
                                fileData,
                                companyData: companyGQL,
                                documentType: type,
                            },
                            client,
                            setFileStatus,
                        }),
                    ),
                ),
            );

            const uploadedIds = readyFiles.filter((_, i) => results[i].status === "fulfilled").map((f) => f.hash);
            const allIds = Array.from(new Set([...uploadedIds, ...alreadyUploadedHashes]));
            finishIdsRef.current = allIds;

            if (uploadedIds.length < readyFiles.length) {
                toast.danger(
                    formatMessage({ id: "app.dms.upload.failed" }, { count: readyFiles.length - uploadedIds.length }),
                );
                return;
            }
            if (uploadedIds.length) {
                toast.success(formatMessage({ id: "app.dms.upload.success" }, { count: uploadedIds.length }));
            }
            // only embedded callers close here; DMS keeps the result on screen until Done
            if (onAutoCloseAfterUploadRef.current) {
                onAutoCloseAfterUploadRef.current(allIds);
                onClose?.();
            }
        },
        [companyGQL, type, client, onClose, formatMessage],
    );

    const finish = useCallback(() => {
        if (finishIdsRef.current.length) {
            onAutoCloseAfterUploadRef.current?.(finishIdsRef.current);
        }
        onClose?.();
    }, [onClose]);

    const handleFilesDrop = useCallback(
        (droppedFiles: Pick<TUploadFile, "file" | "hash">[]) => {
            if (!droppedFiles.length) {
                return;
            }

            // Detect duplicates within this single drop
            const hashSeen = new Set<string>();
            const hashDuplicates = new Set<string>();
            droppedFiles.forEach((f) => {
                if (hashSeen.has(f.hash)) {
                    hashDuplicates.add(f.hash);
                }
                hashSeen.add(f.hash);
            });

            const alreadyUploadedHashes: string[] = [];
            const newFiles: TUploadFile[] = droppedFiles.map((el) => {
                if (documentsMap.has(el.hash)) {
                    alreadyUploadedHashes.push(el.hash);
                    return { ...el, status: UploadFileStatus.ALREADY_UPLOADED };
                }
                if (hashDuplicates.has(el.hash)) {
                    return { ...el, status: UploadFileStatus.NOT_UNIQ };
                }
                return { ...el, status: UploadFileStatus.READY_FOR_UPLOAD };
            });

            alreadyUploadedHashesRef.current = alreadyUploadedHashes;
            finishIdsRef.current = [];
            setAllFilesInfo(newFiles);

            if (!newFiles.some((f) => f.status === UploadFileStatus.NOT_UNIQ)) {
                void uploadFiles(newFiles, alreadyUploadedHashes);
            }
        },
        [documentsMap, uploadFiles],
    );

    // Compute a new file list from the current snapshot and resolution maps,
    // then start upload if all NOT_UNIQ are resolved.
    const resolveNotUniq = useCallback(
        (computeNewState: (prev: TUploadFile[]) => TUploadFile[]) => {
            const newState = computeNewState(allFilesInfoRef.current);
            setAllFilesInfo(newState);
            if (!newState.some((f) => f.status === UploadFileStatus.NOT_UNIQ)) {
                void uploadFiles(newState, alreadyUploadedHashesRef.current);
            }
        },
        [uploadFiles],
    );

    const value: TValue = useMemo(
        () => ({
            allFilesInfo,
            documentType: type,
            queueIsEmpty,
            unsupportedFiles,
            useSingleDocumentMode,
        }),
        [allFilesInfo, type, queueIsEmpty, unsupportedFiles, useSingleDocumentMode],
    );

    const action: TActionValue = useMemo(
        () => ({
            handleFilesDrop,
            setUnsupportedFiles,
            finish,
            confirmChoiceNotUniq: (processedFiles, notUniqFiles) => {
                resolveNotUniq((prev) => {
                    let next = [...prev];
                    Object.keys(processedFiles).forEach((key) => {
                        const without = next.filter((file) => file.hash !== key);
                        const resolved = notUniqFiles[key].map((el, i) => ({
                            ...el,
                            status:
                                i === processedFiles[key]
                                    ? UploadFileStatus.READY_FOR_UPLOAD
                                    : UploadFileStatus.DECLINE,
                        }));
                        next = [...without, ...resolved];
                    });
                    return next;
                });
            },
            deleteAllNotUniq: (processedFiles, notUniqFiles) => {
                resolveNotUniq((prev) => {
                    let next = [...prev];
                    Object.keys(processedFiles).forEach((key) => {
                        const without = next.filter((file) => file.hash !== key);
                        const declined = notUniqFiles[key].map((el) => ({ ...el, status: UploadFileStatus.DECLINE }));
                        next = [...without, ...declined];
                    });
                    return next;
                });
            },
            removeNotUniqGroup: (key, notUniqFiles) => {
                resolveNotUniq((prev) => {
                    const without = prev.filter((file) => file.hash !== key);
                    const declined = notUniqFiles[key].map((el) => ({ ...el, status: UploadFileStatus.DECLINE }));
                    return [...without, ...declined];
                });
            },
        }),
        [handleFilesDrop, resolveNotUniq, finish],
    );

    return (
        <FileUploaderContext value={value}>
            <FileUploaderControlContext value={action}>{children}</FileUploaderControlContext>
        </FileUploaderContext>
    );
};
